import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import {
  fetchEventResults,
  fetchTicketEventStatistics,
  queryKeys,
} from '@/api';
import {
  collectTicketMatchIds,
  eventResultsHasStatistics,
  parseTicketEventResultsStatistics,
  resolveTicketBetLiveDisplay,
  ticketEventStatisticsToPatch,
  type TicketBetLiveDisplay,
  type TicketEventStatisticsDto,
} from '@/domain/my-bets-live';
import type { LiveStatistics, StatPair } from '@/domain/live-statistics';
import type { TicketBetItem, TicketListItem } from '@/domain/tickets';
import {
  useLiveFeedRevision,
  useOptionalLiveFeed,
} from '@/live/LiveFeedProvider';
import { useLiveFeedSocket } from '@/live/useLiveFeedSocket';

type ResultsStats = LiveStatistics & { score?: StatPair };

export type UseMyBetsLiveStatusResult = {
  resolveBetLive: (bet: TicketBetItem) => TicketBetLiveDisplay;
  isStatsOpen: (matchId: number) => boolean;
  toggleStats: (matchId: number) => void;
  /** Call when a ticket expands to seed EventResults statistics. */
  onTicketExpanded: (bets: readonly TicketBetItem[] | null | undefined) => void;
};

function asEventId(value: unknown): number | null {
  const id = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/**
 * Live score/clock enrichment + inline stats for My Bets tickets.
 * Reuses the live feed socket (also started by cashout) and HTTP statistics
 * for open widgets; EventResults seeds stats when a ticket expands.
 */
export function useMyBetsLiveStatus(
  tickets: readonly TicketListItem[],
  enabled: boolean,
  /** Subscribe to fAPI live odds for score/clock (open/live tabs). */
  liveFeedEnabled = enabled,
): UseMyBetsLiveStatusResult {
  const liveFeed = useOptionalLiveFeed();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const isLiveRoute = pathname === '/live' || pathname.startsWith('/live/');

  const matchIds = useMemo(() => {
    const ids = new Set<number>();
    for (const ticket of tickets) {
      for (const id of collectTicketMatchIds(ticket.Bets)) {
        ids.add(id);
      }
    }
    return [...ids];
  }, [tickets]);

  const matchIdsKey = matchIds.join(',');
  useLiveFeedSocket(liveFeedEnabled && matchIds.length > 0 && !isLiveRoute);
  const feedRevision = useLiveFeedRevision(liveFeedEnabled ? matchIds : []);

  const [activeEventIds, setActiveEventIds] = useState<number[]>([]);
  const [resultsByMatchId, setResultsByMatchId] = useState<
    Map<number, ResultsStats>
  >(() => new Map());
  /** MatchIds that EventResults returned with a Statistics payload (legacy show button). */
  const [resultsMatchIds, setResultsMatchIds] = useState<Set<number>>(
    () => new Set(),
  );
  const loadedResultKeysRef = useRef(new Set<string>());
  const [httpByMatchId, setHttpByMatchId] = useState<
    Map<number, ReturnType<typeof ticketEventStatisticsToPatch>>
  >(() => new Map());

  const activeKey = activeEventIds.slice().sort((a, b) => a - b).join(',');

  const statsQuery = useQuery({
    queryKey: queryKeys.tickets.eventStatistics(activeEventIds),
    enabled: enabled && activeEventIds.length > 0,
    queryFn: ({ signal }) => fetchTicketEventStatistics(activeEventIds, signal),
    staleTime: 15_000,
  });

  useEffect(() => {
    if (!statsQuery.isSuccess || !statsQuery.data) return;
    setHttpByMatchId((previous) => {
      const next = new Map(previous);
      for (const event of statsQuery.data as TicketEventStatisticsDto[]) {
        const eventId = asEventId(event.EventId);
        if (eventId == null) continue;
        next.set(eventId, ticketEventStatisticsToPatch({ ...event, EventId: eventId }));
      }
      return next;
    });
  }, [statsQuery.data, statsQuery.dataUpdatedAt, statsQuery.isSuccess]);

  const toggleStats = useCallback((matchId: number) => {
    if (!Number.isFinite(matchId) || matchId <= 0) return;
    setActiveEventIds((current) => {
      if (current.includes(matchId)) {
        return current.filter((id) => id !== matchId);
      }
      return [...current, matchId];
    });
  }, []);

  const isStatsOpen = useCallback(
    (matchId: number) => activeEventIds.includes(matchId),
    [activeEventIds],
  );

  const onTicketExpanded = useCallback(
    (bets: readonly TicketBetItem[] | null | undefined) => {
      const ids = collectTicketMatchIds(bets);
      if (ids.length === 0) return;
      const key = ids.slice().sort((a, b) => a - b).join(',');
      if (loadedResultKeysRef.current.has(key)) return;
      loadedResultKeysRef.current.add(key);
      void fetchEventResults(ids)
        .then((results) => {
          const withStats = new Set<number>();
          setResultsByMatchId((prev) => {
            const merged = new Map(prev);
            for (const event of results ?? []) {
              const eventId = asEventId(event.EventId);
              if (eventId == null) continue;
              if (!eventResultsHasStatistics(event.Statistics)) continue;
              withStats.add(eventId);
              merged.set(
                eventId,
                parseTicketEventResultsStatistics(event.Statistics),
              );
            }
            return merged;
          });
          if (withStats.size > 0) {
            setResultsMatchIds((prev) => {
              const next = new Set(prev);
              for (const id of withStats) next.add(id);
              return next;
            });
          }
        })
        .catch(() => {
          loadedResultKeysRef.current.delete(key);
        });
    },
    [],
  );

  const resolveBetLive = useCallback(
    (bet: TicketBetItem): TicketBetLiveDisplay => {
      const matchId = bet.MatchId;
      const feedPatch =
        matchId != null ? liveFeed?.getStatisticsPatch(matchId) : undefined;
      const feedFinished =
        matchId != null
          ? Boolean(liveFeed?.getEventState(matchId)?.finished)
          : false;
      const http = matchId != null ? httpByMatchId.get(matchId) : undefined;
      const results = matchId != null ? resultsByMatchId.get(matchId) : undefined;

      return resolveTicketBetLiveDisplay(bet, {
        feedPatch,
        feedFinished,
        httpEnrichment: http,
        resultsStatistics: results,
        // Legacy shows the stats button whenever EventResults returned Statistics.
        hasResultsStatistics: matchId != null && resultsMatchIds.has(matchId),
      });
    },
    // feedRevision + activeKey keep displays fresh when socket / HTTP updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional revision deps
    [
      liveFeed,
      httpByMatchId,
      resultsByMatchId,
      resultsMatchIds,
      feedRevision,
      activeKey,
      matchIdsKey,
    ],
  );

  return {
    resolveBetLive,
    isStatsOpen,
    toggleStats,
    onTicketExpanded,
  };
}
